import { Controller, FormProvider, useForm } from "react-hook-form";
import { useMemo, useState } from "react";
import { useBootstrapConfiguration } from "~/app/config/useAppConfig";
import { type DropdownEntry } from "~/common/types";
import { openModal } from "~/common/utils/ModalService";

import { Trans, useTranslation, type WithTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Grid,
  Stack,
  styled,
  TextField,
  Typography,
} from "~/common/mui";

import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import Parameter from "~/common/components/Parameter";
import Checkbox from "~/common/components/Checkbox";
import {
  ADDRESS_MAX_LENGTH,
  CITY_MAX_LENGTH,
  COMPANY_NAME_MAX_LENGTH,
  HOUSENUM_MAX_LENGTH,
  STREET_MAX_LENGTH,
  ZIPCODE_MAX_LENGTH,
  ZIPCODE_MIN_LENGTH,
  ZIPCODE_REGEXP,
} from "~/common/utils/validations";
import { makeSelectRef4RHF } from "~/features/myAccount/profile/formUtils";
import { LinkButton } from "~/common/components/Link";
import { splitAddressLine1 } from "~/utils/addressUtils";
import type { AddressData } from "../../Address";
import DeleteIcon from "@mui/icons-material/Delete";

interface Props {
  onClose: () => void;
  onSave: (address: AddressForm & { id?: string }) => void;
  onDelete: (address: AddressData) => void;
  address: AddressData | null;
  countriesList: {
    isocode: string;
    name: string;
  }[];
}

interface AddressForm {
  companyName: string;
  street: string;
  houseNumber: string;
  addressAdditional: string;
  zipCode: string;
  city: string;
  country: string;
  persistForLater: boolean;
}

function makeInitialFormData(address: AddressData | null, countryOptions: DropdownEntry<string>[]): AddressForm {
  const { houseNumber, street } = splitAddressLine1(address?.lines[0] ?? "");

  let country = address?.country.isocode ?? "";
  if (!country && countryOptions.length === 1) {
    country = countryOptions[0].value;
  }

  return {
    companyName: address?.companyName ?? "",
    zipCode: address?.postalCode ?? "",
    addressAdditional: address?.lines[1] ?? "",
    city: address?.town ?? "",
    country,
    street,
    houseNumber,
    persistForLater: !address?.temporary,
  };
}

function processCountries(countriesList: Props["countriesList"], t: WithTranslation["t"]): DropdownEntry<string>[] {
  return countriesList.map((country) => ({
    label: t(country.name),
    value: country.isocode,
  }));
}

export const CreateEditAddressModal = (props: Props) => {
  const { t } = useTranslation();
  const isHouseNumberFirst = useBootstrapConfiguration()?.houseNumberFirstInAddress ?? false;
  const countryOptions = useMemo(() => processCountries(props.countriesList, t), [props.countriesList]);

  const { onClose, onSave, onDelete } = props;
  const [initialData] = useState(() => makeInitialFormData(props.address, countryOptions));
  const id = props.address?.id;

  const isEditMode = !!id;

  const formHooks = useForm<AddressForm>({ defaultValues: initialData });

  const onDeleteCb = async () => {
    if (await requestConfirmation()) {
      onDelete(props.address!);
      onClose();
    }
  };

  const submitCb = formHooks.handleSubmit((data) => {
    onSave({ ...data, id });
  });

  const makeErrorMessage = (suffix: string) => (fieldCode: string) =>
    t(`checkoutPage.addressModal.input.error.${suffix}`, {
      fieldname: t(`checkoutPage.addressModal.input.label.${fieldCode}`).toLowerCase(),
    });

  const makeTooLongMessage = (fieldCode: string) =>
    t(`checkoutPage.addressModal.error.isTooLongPart`, {
      fieldname: t(`checkoutPage.addressModal.input.label.${fieldCode}`),
    });
  const makePleaseEnterMessage = (fieldCode: string) =>
    t(`checkoutPage.addressModal.error.pleaseEnter`, {
      fieldname: t(`checkoutPage.addressModal.input.label.${fieldCode}`).toLowerCase(),
    });

  return (
    <Dialog open={true} maxWidth="desktop-s">
      <DialogHeader>
        <DialogTitle>{t(`checkoutPage.addressModal.${isEditMode ? "edit" : "add"}.title`)}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <FormProvider {...formHooks}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <Controller
                name={"companyName"}
                rules={{
                  required: makePleaseEnterMessage("companyName"),
                  maxLength: {
                    value: COMPANY_NAME_MAX_LENGTH,
                    message: makeTooLongMessage("companyName"),
                  },
                }}
                render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    inputProps={{ "data-test-id": "address-modal-input-company" }}
                    label={t("checkoutPage.addressModal.input.label.companyName") + "*"}
                    inputRef={ref}
                    {...rest}
                    helperText={error?.message}
                    error={!!error}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <Controller
                name={"street"}
                rules={{
                  required: makePleaseEnterMessage("street"),
                  maxLength: {
                    value: STREET_MAX_LENGTH,
                    message: makeTooLongMessage("street"),
                  },
                }}
                render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    inputProps={{ "data-test-id": "address-modal-input-address-street" }}
                    label={t("checkoutPage.addressModal.input.label.street") + "*"}
                    inputRef={ref}
                    {...rest}
                    helperText={error?.message}
                    error={!!error}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <Controller
                name={"houseNumber"}
                rules={{
                  required: makePleaseEnterMessage("houseNumber"),
                  maxLength: {
                    value: HOUSENUM_MAX_LENGTH,
                    message: makeTooLongMessage("houseNumber"),
                  },
                }}
                render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    style={{ order: isHouseNumberFirst ? -1 : 1 }}
                    inputProps={{ "data-test-id": "address-modal-input-address-house-num" }}
                    label={t("checkoutPage.addressModal.input.label.houseNumber") + "*"}
                    inputRef={ref}
                    {...rest}
                    helperText={error?.message}
                    error={!!error}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                name={"addressAdditional"}
                rules={{
                  maxLength: {
                    value: ADDRESS_MAX_LENGTH,
                    message: makeTooLongMessage("addressAdditional"),
                  },
                }}
                render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    inputProps={{ "data-test-id": "address-modal-input-address-additional" }}
                    label={t("checkoutPage.addressModal.input.label.addressAdditional")}
                    inputRef={ref}
                    {...rest}
                    helperText={error?.message}
                    error={!!error}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <Controller
                name={"zipCode"}
                rules={{
                  required: makePleaseEnterMessage("zipCode"),
                  minLength: {
                    value: ZIPCODE_MIN_LENGTH,
                    message: makeErrorMessage("tooShortPart")("zipCode"),
                  },
                  maxLength: {
                    value: ZIPCODE_MAX_LENGTH,
                    message: makeTooLongMessage("zipCode"),
                  },
                  pattern: {
                    value: ZIPCODE_REGEXP,
                    message: makeErrorMessage("forbiddenCharactersPart")("zipCode"),
                  },
                }}
                render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    inputProps={{ "data-test-id": "address-modal-input-zip" }}
                    label={t("checkoutPage.addressModal.input.label.zipCode") + "*"}
                    inputRef={ref}
                    {...rest}
                    helperText={error?.message}
                    error={!!error}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <Controller
                name={"city"}
                rules={{
                  required: makePleaseEnterMessage("city"),
                  maxLength: {
                    value: CITY_MAX_LENGTH,
                    message: makeTooLongMessage("city"),
                  },
                }}
                render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    inputProps={{ "data-test-id": "address-modal-input-city" }}
                    label={t("checkoutPage.addressModal.input.label.city") + "*"}
                    inputRef={ref}
                    {...rest}
                    helperText={error?.message}
                    error={!!error}
                  />
                )}
              />
            </Grid>
            {countryOptions.length > 1 && (
              <Grid size={12}>
                <Controller
                  name={"country"}
                  rules={{
                    required: makePleaseEnterMessage("country"),
                  }}
                  render={({ field: { onChange, ref, ...rest }, fieldState: { error } }) => (
                    <SimpleSelect
                      label={t("checkoutPage.addressModal.input.label.country") + "*"}
                      data-test-id={"address-modal-dropdown-country"}
                      values={countryOptions}
                      error={!!error}
                      helperText={error?.message}
                      onChange={(e) => onChange(e.target.value)}
                      ref={makeSelectRef4RHF(ref)}
                      {...rest}
                    />
                  )}
                />
              </Grid>
            )}
            {countryOptions.length === 1 && (
              <Grid size={12}>
                <Parameter label={t("checkoutPage.addressModal.input.label.country")}>
                  <Typography variant="bodyLarge">{countryOptions[0].label}</Typography>
                </Parameter>
              </Grid>
            )}
            {isEditMode && (
              <Grid size={12}>
                <LinkButton data-test-id={"address-modal-btn-delete"} onClick={onDeleteCb} iconBefore={<DeleteIcon />}>
                  {t("checkoutPage.addressModal.delete")}
                </LinkButton>
              </Grid>
            )}
            {!isEditMode && (
              <>
                <Grid size={12}>
                  <Controller
                    name={"persistForLater"}
                    render={({ field: { value, onChange } }) => (
                      <Stack direction={"row"} gap={1} alignItems={"center"}>
                        <Checkbox
                          data-test-id={"address-modal-cbox-save-for-later"}
                          checked={value}
                          onCheck={onChange}
                        />
                        <Typography>{t("checkoutPage.addressModal.saveForLater")}</Typography>
                      </Stack>
                    )}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <FooterStack>
          <Typography>{"*" + t("checkoutPage.addressModal.mandatory")}</Typography>
          <Stack direction={"row"} gap={1}>
            <Button variant={"text"} data-test-id={"address-modal-btn-cancel"} onClick={onClose}>
              {t("checkoutPage.addressModal.cancel")}
            </Button>
            <Button data-test-id={"address-modal-btn-save"} onClick={submitCb}>
              {t("checkoutPage.addressModal.save")}
            </Button>
          </Stack>
        </FooterStack>
      </DialogActions>
    </Dialog>
  );
};

function requestConfirmation() {
  return new Promise((resolve) => {
    openModal("Address-delete-confirmation", (close) => {
      const onClose = () => {
        close();
        resolve(false);
      };
      const onConfirm = () => {
        close();
        resolve(true);
      };

      return (
        <Dialog open={true} maxWidth="desktop-s">
          <DialogHeader>
            <Typography variant="headlineSmall">
              <Trans i18nKey={"checkoutPage.addressModal.info.areYouSure"} />
            </Typography>
          </DialogHeader>
          <DialogActions>
            <Stack direction="row" gap={1}>
              <Button variant={"text"} data-test-id={"address-modal-btn-no"} onClick={onClose}>
                <Trans i18nKey={"checkoutPage.addressModal.no"} />
              </Button>
              <Button data-test-id={"address-modal-btn-no"} onClick={onConfirm}>
                <Trans i18nKey={"checkoutPage.addressModal.yes"} />
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>
      );
    });
  });
}

const FooterStack = styled(Stack)({
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%",
});
